import React, { useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

/**
 * Modal zmiany hasla dla zalogowanego uzytkownika.
 * Props:
 *  - isOpen: bool
 *  - onClose: fn
 *  - mustChange: bool - jesli true, nie mozna zamknac (wymuszona zmiana)
 *  - daysLeft: number | null
 */
export default function ChangePasswordModal({ isOpen, onClose, mustChange = false, daysLeft = null }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Nowe hasło i potwierdzenie się różnią');
      return;
    }
    if (newPassword.length < 8) {
      setError('Hasło musi mieć przynajmniej 8 znaków');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      toast.success(resp.data?.message || 'Hasło zmienione');
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      if (onClose) onClose();
      // Force reload żeby odświeżyć sesję (must_change_password odznaczony)
      if (mustChange) {
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd zmiany hasła');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={mustChange ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--card-bg)', borderRadius: '8px', padding: '1.5rem',
          maxWidth: '440px', width: '94%', boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        }}
      >
        <h5 style={{ margin: '0 0 0.75rem', fontSize: '16px', fontWeight: '700' }}>
          <i className="fas fa-key" style={{ marginRight: '6px', color: 'var(--info)' }}></i>
          Zmiana hasła
        </h5>

        {mustChange && (
          <div style={{
            padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '12px',
            backgroundColor: 'var(--warning-light)', color: 'var(--warning)',
            border: '1px solid var(--warning)', borderRadius: '4px',
          }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
            <b>Twoje hasło wygasło.</b> Aby kontynuować pracę musisz ustawić nowe hasło.
          </div>
        )}

        {!mustChange && daysLeft !== null && daysLeft >= 0 && daysLeft <= 7 && (
          <div style={{
            padding: '0.4rem 0.6rem', marginBottom: '0.75rem', fontSize: '11px',
            backgroundColor: 'var(--info-light)', color: 'var(--info)', borderRadius: '4px',
          }}>
            <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
            Twoje hasło wygaśnie za <b>{daysLeft} {daysLeft === 1 ? 'dzień' : 'dni'}</b>.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: '0.6rem', fontSize: '12px' }}>
            <div style={{ marginBottom: '3px', fontWeight: '600' }}>Stare hasło</div>
            <input
              type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
              required autoFocus autoComplete="current-password"
              style={{
                width: '100%', padding: '0.5rem 0.6rem', fontSize: '13px',
                border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box',
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '0.6rem', fontSize: '12px' }}>
            <div style={{ marginBottom: '3px', fontWeight: '600' }}>Nowe hasło</div>
            <input
              type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              required minLength={8} autoComplete="new-password"
              style={{
                width: '100%', padding: '0.5rem 0.6rem', fontSize: '13px',
                border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box',
              }}
            />
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>
              Min. 8 znaków. Wymaga liter i cyfr. Nie może być jednym z 3 ostatnich używanych.
            </div>
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '12px' }}>
            <div style={{ marginBottom: '3px', fontWeight: '600' }}>Powtórz nowe hasło</div>
            <input
              type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              required autoComplete="new-password"
              style={{
                width: '100%', padding: '0.5rem 0.6rem', fontSize: '13px',
                border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box',
              }}
            />
          </label>

          {error && (
            <div style={{
              padding: '0.4rem 0.6rem', marginBottom: '0.75rem', fontSize: '11px',
              backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px',
            }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            {!mustChange && (
              <button
                type="button" onClick={onClose} disabled={submitting}
                style={{
                  padding: '0.5rem 1rem', fontSize: '12px', borderRadius: '4px',
                  border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >Anuluj</button>
            )}
            <button
              type="submit" disabled={submitting}
              style={{
                padding: '0.5rem 1rem', fontSize: '12px', borderRadius: '4px', fontWeight: '700',
                border: '1px solid var(--info)', backgroundColor: submitting ? '#999' : 'var(--info)',
                color: '#fff', cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Zapisywanie...' : 'Zmień hasło'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
